import { createApi } from "@reduxjs/toolkit/query/react";

import { createAuthBaseQuery } from "../../common/utils";
import { HOSTNAME } from "../../constant/host";

/**
 * AI 画像生成 API。
 *
 * 画像シーン（アバター / プロジェクトバナー背景 / プロジェクトロゴ /
 * プロフィール作品アートワーク）に従って GPT Image 系モデルで候補画像を
 * 生成し、S3 に保存して presigned URL を返す。ユーザーは 1 枚を選び、
 * 既存の保存 API（PATCH /api/me / works CRUD / プロジェクト PATCH の
 * banner_image_key / banner_logo_key）で file_key を確定する。
 *
 * 希望テキスト（user_hint）は任意。空でも管理者設定のプロンプト + DB 文脈
 * （プロフィール / プロジェクト情報 / 作品情報）だけで生成できる。
 *
 * シーン一覧（BE の app/app_services/ai/scenes.py と対応）:
 *   - `image_avatar`         : アバター画像（1024x1024、スタイル 3 案。
 *                              現アバター写真を元に生成可能）
 *   - `image_project_banner` : プロジェクトバナー背景画像（1536x1024、project_id 必須）
 *   - `image_project_logo`   : プロジェクトロゴ（透過 PNG・白単色、project_id 必須）
 *   - `image_profile_work`   : プロフィール作品アートワーク（1024x1024、
 *                              既存作品のテイストを参照）
 *   - `image_estimate_item`  : ステップ見積もりの項目サムネイル（1536x1024、
 *                              KINFOLK 風のミニマルなキービジュアル）
 */

// 利用可能な画像シーンキー（BE の app/app_services/ai/scenes.py と対応）
export type AIImageScene =
  | "image_avatar"
  | "image_project_banner"
  | "image_project_logo"
  | "image_profile_work"
  | "image_estimate_item";

/** 出力サイズの形状（BE の IMAGE_SHAPE_SIZES と対応） */
export type AIImageShape = "square" | "portrait" | "landscape";

export interface GenerateImagesParams {
  /** 画像シーンキー（例: "image_avatar"） */
  scene: AIImageScene;
  /** ユーザーの希望イメージ（任意。空でも生成可） */
  user_hint?: string;
  /** プロジェクトID（image_project_banner / image_project_logo で必須） */
  project_id?: string | null;
  /** 作品タイトル（image_profile_work の文脈。任意） */
  work_title?: string | null;
  /** 作品タグ（image_profile_work の文脈。任意） */
  work_tags?: string[] | null;
  /** 作品説明（image_profile_work の文脈。任意） */
  work_desc?: string | null;
  /** 編集中の作品ID（既存アートワーク参照から除外する。任意） */
  work_id?: string | null;
  /** image_avatar: 現在のアバター写真を元に生成するか（デフォルト true） */
  use_current_avatar?: boolean;
  /** 対象の項目名（image_estimate_item の文脈。任意） */
  item_label?: string | null;
  /** 項目の補足説明（image_estimate_item の文脈。任意） */
  item_desc?: string | null;
  /** 見積もり全体の項目名一覧（image_estimate_item の文脈。任意） */
  plan_items?: string[] | null;
  /**
   * 生成する候補枚数（1〜3、既定 3）。1 枚ずつのリクエストを並列に投げると、
   * できたものから順に表示できる（全部揃うまで待たない）。
   * レート制限は枚数で数えるので、分割してもコスト上限は変わらない。
   */
  candidate_count?: number;
  /** シーン既定の出力サイズを上書きする形状（正方形で使うサムネイル向け） */
  image_shape?: AIImageShape;
}

export interface GeneratedImageCandidate {
  /** S3 の file_key（選択後、既存の保存 API に渡す） */
  file_key: string;
  /** プレビュー用 presigned URL（1時間有効） */
  url: string;
}

export interface GenerateImagesData {
  candidates: GeneratedImageCandidate[];
  scene: string;
  provider: string;
  model: string;
}

export interface GenerateImagesResponse {
  status: string;
  data: GenerateImagesData;
}

export const aiImageApi = createApi({
  reducerPath: "aiImageApi",
  baseQuery: createAuthBaseQuery(HOSTNAME),
  endpoints: (builder) => ({
    generateImages: builder.mutation<GenerateImagesResponse, GenerateImagesParams>({
      query: ({
        scene,
        user_hint,
        project_id,
        work_title,
        work_tags,
        work_desc,
        work_id,
        use_current_avatar,
        item_label,
        item_desc,
        plan_items,
        candidate_count,
        image_shape,
      }) => ({
        url: `/api/ai/image-generate`,
        method: "POST",
        body: {
          scene,
          ...(user_hint ? { user_hint } : {}),
          ...(project_id ? { project_id } : {}),
          ...(work_title ? { work_title } : {}),
          ...(work_tags && work_tags.length > 0 ? { work_tags } : {}),
          ...(work_desc ? { work_desc } : {}),
          ...(work_id ? { work_id } : {}),
          ...(use_current_avatar === false ? { use_current_avatar: false } : {}),
          ...(item_label ? { item_label } : {}),
          ...(item_desc ? { item_desc } : {}),
          ...(plan_items && plan_items.length > 0 ? { plan_items } : {}),
          ...(candidate_count ? { candidate_count } : {}),
          ...(image_shape ? { image_shape } : {}),
        },
      }),
    }),
  }),
});

export const { useGenerateImagesMutation } = aiImageApi;
